import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Mail, Lock, User, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import logoFull from "@/assets/logo-full.png";

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const nav = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register({ email, password, name });
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      nav("/app");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0B0D11] text-[#F3EFE6] flex flex-col items-center justify-center px-5 rp-noise">
      <Link to="/" className="mb-8" data-testid="login-logo-link">
        <img src={logoFull} alt="Rodeo Intel" className="h-28 w-auto" />
      </Link>

      <div className="w-full max-w-md rounded-2xl border border-[#262933] bg-[#12141A] p-7 shadow-2xl shadow-black/40">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E8833A] mb-1">
          {mode === "login" ? "Sign in" : "Create account"}
        </div>
        <h1 className="font-display font-extrabold text-2xl mb-6">
          {mode === "login" ? "Welcome back" : "Get started"}
        </h1>

        <button
          data-testid="google-login-button"
          onClick={google}
          className="w-full flex items-center justify-center gap-3 h-11 rounded-lg bg-white text-[#1A1D26] font-semibold text-sm hover:bg-[#f1f1f1] transition-colors"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/></svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-5">
          <div className="h-px flex-1 bg-[#262933]" />
          <span className="text-[11px] font-mono uppercase tracking-widest text-[#686B77]">or</span>
          <div className="h-px flex-1 bg-[#262933]" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <Field icon={User} testid="register-name" type="text" placeholder="Your name"
                   value={name} onChange={setName} />
          )}
          <Field icon={Mail} testid="login-email" type="email" placeholder="you@email.com"
                 value={email} onChange={setEmail} required />
          <Field icon={Lock} testid="login-password" type="password" placeholder="Password"
                 value={password} onChange={setPassword} required />
          <button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="w-full flex items-center justify-center gap-2 h-11 rounded-lg font-semibold bg-[#E8833A] text-[#0B0D11] hover:bg-[#F0924A] transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            {mode === "login" ? "Sign in" : "Create account"}
            {!busy && <ArrowRight className="h-4 w-4" />}
          </button>
        </form>

        <div className="mt-5 text-center text-sm text-[#9A9CA6]">
          {mode === "login" ? "New to Rodeo Intel?" : "Already have an account?"}{" "}
          <button
            data-testid="toggle-auth-mode"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="text-[#E8833A] font-semibold hover:text-[#F0924A]"
          >
            {mode === "login" ? "Create an account" : "Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ icon: Icon, testid, value, onChange, ...rest }) => (
  <div className="relative">
    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#686B77]" />
    <input
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-11 rounded-lg bg-[#0F1116] border border-[#262933] pl-10 pr-4 text-sm focus:outline-none focus:border-[#E8833A]"
      {...rest}
    />
  </div>
);
